// src/pages/reports/components/PMDrawer.jsx
import React, { useMemo } from 'react';
import {
  Box,
  HStack,
  VStack,
  Text,
  Heading,
  Badge,
  Tag,
  TagLabel,
  Button,
  ButtonGroup,
  FormControl,
  FormLabel,
  Switch,
  Input,
  Textarea,
  Tooltip,
  SimpleGrid,
  Link as ChakraLink,
  Wrap,
  
  
  
} from '@chakra-ui/react';
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerCloseButton,
} from '@chakra-ui/react';

// NEW: inline basket renderer (already present in your tree)

// START OF ADDITIONS
import PMDrawerAccordion from "./PMDrawerAccordion";
import useOrgClaims from "../../../hooks/useOrgClaims";
// END OF ADDITIONS

function fmtDateShort(ts) {
  if (!ts) return '—';
  const d = typeof ts?.toDate === 'function' ? ts.toDate() : new Date(ts);
  if (Number.isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
}
function fmtTime24(ts) {
  if (!ts) return '—';
  const d = typeof ts?.toDate === 'function' ? ts.toDate() : new Date(ts);
  if (Number.isNaN(d.getTime())) return '—';
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mi}`;
}
function fmtDateTime(ts) {
  if (!ts) return '—';
  const d = typeof ts?.toDate === 'function' ? ts.toDate() : new Date(ts);
  if (Number.isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yy} ${hh}:${mi}`;
}
function isServerTsSentinel(v) {
  return v && typeof v === 'object' && v._methodName === 'serverTimestamp';
}
function money(v) {
  const n = Number(v);
  if (Number.isFinite(n)) return `£${n.toLocaleString('en-GB', { maximumFractionDigits: 2 })}`;
  return String(v ?? '—');
}
function phoneFromLead(lead) {
  if (!lead) return '';
  return (
    lead.phone ||
    lead.customerPhone ||
    lead.mobile ||
    lead.tel ||
    (lead.contact && lead.contact.phone) ||
    (lead.phones && lead.phones.primary) ||
    ''
  );
}
function telHref(phone) {
  const p = String(phone || '').replace(/[^\d+]/g, '');
  return p ? `tel:${p}` : '';
}

// Reason codes → labels (matches mobile modal)
const REASON_LABELS_BY_CODE = {
  think_about_it: 'Think about it',
  cant_afford: "Can’t afford it",
  competitor_better: 'Better offer from competitors',
  want_more_quotes: 'Want more quotes',
  single_head: 'Single Head',
};

export default function PMDrawer({
  // shell
  isOpen,
  onClose,
  initialTab = 'details',
  // lead + context
  lead,
  pmDoc,
  pmNotes = [],          // conversation history list (latest first)
  // log form state (controlled by parent)
  called,
  onCalledChange,
  answered,
  onAnsweredChange,
  priceOffered,
  onPriceOfferedChange,
  newPrice,
  onNewPriceChange,
  notes,
  onNotesChange,
  nextCallDate,
  onNextCallDateChange,
  // actions (from parent)
  onSave,
  onOpenSalesReport,     // required by our PM flow
}) {
  const salesReportEnabled = Boolean(called && answered && priceOffered);

  // NEW: disclosure state for the inline basket section

  // START OF ADDITION
  const { orgId } = useOrgClaims();
  // END OF ADDITION

  const customer = useMemo(() => {
    if (!lead) return '—';
    return (
      lead.customer ||
      lead.customerFullName ||
      `${lead.customerFirstName || ''} ${lead.customerLastName || ''}`.trim() ||
      '—'
    );
  }, [lead]);

  const phone = phoneFromLead(lead);

  // Pull known fields out of pmDoc for a tidy display
  const pm = pmDoc || {};
  const display = {
    status: pm.status,
    productsQuoted: pm.productsQuoted,         // now used as Items summary
    priceQuoted: pm.priceQuoted,
    reasonNotBuying: pm.reasonNotBuying,       // legacy string fallback
    reasonNotBuyingCodes: Array.isArray(pm.reasonNotBuyingCodes) ? pm.reasonNotBuyingCodes : [],
    competitorName: pm.competitorName,
    competitorPriceText: pm.competitorPriceText,
    createdAt: isServerTsSentinel(pm.createdAt) ? null : pm.createdAt,
    updatedAt: isServerTsSentinel(pm.updatedAt) ? null : pm.updatedAt,
    salespersonName: pm.salespersonName,
  };

  // Derive reasons to show as badges
  const reasonLabelsFromCodes =
    display.reasonNotBuyingCodes.map((c) => REASON_LABELS_BY_CODE[c]).filter(Boolean);

  const legacyReasonList =
    (!reasonLabelsFromCodes.length && typeof display.reasonNotBuying === 'string')
      ? display.reasonNotBuying.split(',').map(s => s.trim()).filter(Boolean)
      : [];

  // Decide whether to show competitor block
  const competitorSelected = display.reasonNotBuyingCodes.includes('competitor_better');
  const hasCompetitorBlock =
    competitorSelected || display.competitorName || display.competitorPriceText;

  return (
    <Drawer isOpen={isOpen} placement="right" size="lg" onClose={onClose}>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>
          <HStack justify="space-between" align="center">
            <VStack align="start" spacing={0}>
              <Heading size="md">{customer}</Heading>
              <HStack spacing={2} mb={1}>
                <Tag colorScheme="orange">{(lead?.status || 'PM').toUpperCase()}</Tag>
                {lead?.postcode && <Badge colorScheme="gray">{lead.postcode}</Badge>}
                {lead?.salespersonName && (
                  <Badge colorScheme="purple">{lead.salespersonName}</Badge>
                )}
              </HStack>
              {phone && (
                <ChakraLink href={telHref(phone)} color="teal.600">
                  {phone}
                </ChakraLink>
              )}
            </VStack>
          </HStack>
        </DrawerHeader>

        <DrawerBody>
          <VStack align="stretch" spacing={5}>
            {/* Sales Rep Notes (moved here, includes appointment date/time) */}
            {(pm && Object.keys(pm).length > 0) || lead?.apptDate ? (
              <Box>
                <Heading size="md" mb={2} color="blue.600">Brief:</Heading>
                <Box
                  bg="gray.50"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  p={3}
                >
                  <SimpleGrid columns={[1, 2]} spacing={2} fontSize="sm">
                    {/* Appointment inline */}
                    <Text><strong>Appointment:</strong> {fmtDateShort(lead?.apptDate)} {fmtTime24(lead?.apptDate)}</Text>

                    {display.salespersonName && (
                      <Text><strong>Salesperson:</strong> {String(display.salespersonName)}</Text>
                    )}
                    {display.status && (
                      <Text><strong>Status:</strong> {String(display.status)}</Text>
                    )}

                    {/* Items summary (read-only, from pm.productsQuoted) */}
                    <Text><strong>Items:</strong> {display.productsQuoted ? String(display.productsQuoted) : '—'}</Text>

                    {display.priceQuoted !== undefined && display.priceQuoted !== '' && (
                      <Text><strong>Price quoted:</strong> {money(display.priceQuoted)}</Text>
                    )}

                    {/* Reasons badges (codes preferred, fallback to legacy string) */}
                    {(reasonLabelsFromCodes.length > 0 || legacyReasonList.length > 0) && (
                      <Box gridColumn={['auto', '1 / -1']}>
                        <Text><strong>Reasons not buying?</strong></Text>
                        <Wrap mt={1} spacing="6px">
                          {(reasonLabelsFromCodes.length ? reasonLabelsFromCodes : legacyReasonList).map((lbl) => (
                            <Tag key={lbl} size="sm" colorScheme="purple">
                              <TagLabel>{lbl}</TagLabel>
                            </Tag>
                          ))}
                        </Wrap>
                      </Box>
                    )}

                    {/* Competitor block (only if relevant) */}
                    {hasCompetitorBlock && (
                      <Box gridColumn={['auto', '1 / -1']}>
                        <Text><strong>Competitor:</strong> {display.competitorName || '—'}</Text>
                        <Text><strong>Competitor price:</strong> {display.competitorPriceText || '—'}</Text>
                      </Box>
                    )}

                    {(display.createdAt || display.updatedAt) && (
                      <>
                        {display.createdAt && (
                          <Text><strong>Created:</strong> {fmtDateTime(display.createdAt)}</Text>
                        )}
                        {display.updatedAt && (
                          <Text><strong>Updated:</strong> {fmtDateTime(display.updatedAt)}</Text>
                        )}
                      </>
                    )}
                  </SimpleGrid>
                </Box>
              </Box>
            ) : null}

            {/* START OF REPLACEMENT */}
            <Box>
              <Heading size="md" mb={2} color="blue.600">Click to Dive In</Heading>
              <PMDrawerAccordion orgId={orgId} lead={lead} />
            </Box>
            {/* END OF REPLACEMENT */}

            {/* Log call attempt */}
            <Box>
              <Heading size="sm" mb={3}>Log Call Attempt</Heading>
              <VStack align="stretch" spacing={3}>
                <HStack>
                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb="0">Called</FormLabel>
                    <Switch isChecked={called} onChange={(e) => onCalledChange?.(e.target.checked)} />
                  </FormControl>
                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb="0">Answered</FormLabel>
                    <Switch isChecked={answered} onChange={(e) => onAnsweredChange?.(e.target.checked)} />
                  </FormControl>
                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb="0">Price offered</FormLabel>
                    <Switch isChecked={priceOffered} onChange={(e) => onPriceOfferedChange?.(e.target.checked)} />
                  </FormControl>
                </HStack>

                <HStack spacing={3}>
                  <FormControl isDisabled={!priceOffered} maxW="240px">
                    <FormLabel>New price (£)</FormLabel>
                    <Input
                      inputMode="decimal"
                      placeholder="e.g. 2450"
                      value={newPrice}
                      onChange={(e) => onNewPriceChange?.(e.target.value)}
                    />
                  </FormControl>
                  <FormControl maxW="280px">
                    <FormLabel>Next call</FormLabel>
                    <Input
                      type="datetime-local"
                      value={nextCallDate || ''}
                      onChange={(e) => onNextCallDateChange?.(e.target.value)}
                    />
                  </FormControl>
                </HStack>

                <FormControl>
                  <FormLabel>Keep Notes!</FormLabel>
                  <Textarea
                    rows={3}
                    value={notes}
                    onChange={(e) => onNotesChange?.(e.target.value)}
                    placeholder="Prices offered, objections, what to try next…"
                  />
                </FormControl>
              </VStack>
            </Box>

            {/* Conversation history (newest first; show price when present) */}
            <Box>
              <Heading size="sm" mb={2}>Conversation History</Heading>
              {Array.isArray(pmNotes) && pmNotes.length > 0 ? (
                <VStack align="stretch" spacing={2}>
                  {pmNotes.map((n) => (
                    <Box
                      key={n.id || `${n.createdByUid || 'user'}-${n.createdAt?.seconds || Math.random()}`}
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="md"
                      p={3}
                      bg="white"
                    >
                      <HStack justify="space-between" mb={1}>
                        <Text fontWeight="semibold">
                          {n.createdByName || 'User'}
                        </Text>
                        <Text color="gray.500" fontSize="sm">
                          {fmtDateTime(n.createdAt)}
                        </Text>
                      </HStack>
                      {typeof n.newPrice === 'number' && !Number.isNaN(n.newPrice) && (
                        <Text fontSize="sm" color="gray.700" mb={1}>
                          <strong>New price:</strong> £{Number(n.newPrice).toLocaleString('en-GB', { maximumFractionDigits: 2 })}
                        </Text>
                      )}
                      <Text whiteSpace="pre-wrap">{n.text || ''}</Text>
                    </Box>
                  ))}
                </VStack>
              ) : (
                <Text color="gray.600">No conversations yet.</Text>
              )}
            </Box>
          </VStack>
        </DrawerBody>

        <DrawerFooter>
          <HStack w="100%" justify="space-between">
            <ButtonGroup>
              <Button variant="outline" onClick={onClose}>Close</Button>
              <Button colorScheme="blue" onClick={onSave}>Save log</Button>
            </ButtonGroup>

            <HStack spacing={3}>

              <Tooltip
                label={!salesReportEnabled ? 'Enable by setting: Called + Answered + Price offered' : ''}
                isDisabled={salesReportEnabled}
                hasArrow
              >
                <Button
                  colorScheme="purple"
                  onClick={onOpenSalesReport}
                  isDisabled={!salesReportEnabled}
                >
                  Job Won!
                </Button>
              </Tooltip>
            </HStack>
          </HStack>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}